import { useCallback, useEffect, useState } from 'react';

import { localizeContent } from 'api/localizations';
import useLanguageRegionCode from './useLanguageRegionCode';

function parseTable(table: string): string {
  switch (table) {
    case 'waypoints':
      return 'at_map_markers';
    case 'lists':
      return 'lists';
    case 'activities':
      return 'at_maps';
    case 'reviews':
      return 'trail_reviews';
    case 'trails':
      return 'trails';
    default:
      return null;
  }
}

function handleParseError() {
  return new Error('Table needs to be a string and one of: waypoints, lists, activities, trails or reviews.');
}

/**
 * Handles the API call to GET localized content based off a locale. If the localized content does not exist
 * the API creates the content requested.
 * @param {string} text The localized text that can be to be translated.
 * @param {string} language The language the unlocalized text originated as. Comparator to user locale and source language provided.
 * @param {string} table The table type associated to the localized content. @see parseTable for options.
 * @param {string} name The name of the type of associated data such as a comment, description, or title.
 * @param {string} rowId The associated rowId such as a particular review, map ID, or unique ID.
 * @param {string} loadOnMount Load on mount of functional component otherwise load when called as default.
 * @returns { localizedData, loadLocalizedData, isLoading, toggledLocaleText, toggleText, isTranslatable, localizedText }
 */
function useLocalizedText(text = '', language = '', table = '', name = '', rowId = '', loadOnMount = false) {
  const [localizedData, setLocalizedData] = useState(null);
  const [toggledLocaleText, setToggleLocaleText] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [textData, setTextData] = useState(text);
  const locale = useLanguageRegionCode();
  const tableData = parseTable(table);

  if (!tableData) handleParseError();

  const loadLocalizedData = useCallback(async () => {
    if (localizedData) return;
    setIsLoading(true);

    try {
      const data = await localizeContent(tableData, name, rowId, locale);
      setLocalizedData(data);
      setIsLoading(false);
      setTextData(data[0] && data[0].content);
    } catch (err) {
      setLocalizedData(err);
      setIsLoading(false);
    }
  }, [tableData, name, rowId, locale, localizedData]);

  const handleToggleText = async () => {
    if (!localizedData) await loadLocalizedData();
    setToggleLocaleText(!toggledLocaleText);
  };

  useEffect(() => {
    if (loadOnMount && !localizedData) loadLocalizedData();
  }, [localizedData, loadLocalizedData, loadOnMount]);

  return {
    localizedData,
    loadLocalizedData,
    isLoading,
    toggledLocaleText,
    toggleText: handleToggleText,
    isTranslatable: language && language !== locale,
    localizedText: !toggledLocaleText ? text : textData
  };
}

export default useLocalizedText;
